// Live "N of M characters left" counter under every <textarea maxlength>, site-wide
// (public forms and staff-info pages alike — this file is bundled into main.js).
//
// A textarea is skipped when it already has its own counter (any element with
// "count" in its class inside the same parent — careers .f__count, .char-counter,
// .vg-char-counter, .slide-cue-char-counter, …), is readonly, or carries
// data-char-counter="off". Textareas added later (JS-built rows, modals) and
// maxlength changes (the contact modal sets it per form when it opens) are
// picked up by a MutationObserver.
import { domReady } from '../utils/dom.js';

const COUNTER_CLASS = 'char-count';
const BOUND = new WeakMap();

function nearThreshold(max) {
    return Math.max(25, Math.round(max * 0.1));
}

function hasOwnCounter(area) {
    const parent = area.parentElement;
    if (!parent) return false;
    return [...parent.querySelectorAll('[class*="count"]')]
        .some(el => !el.classList.contains(COUNTER_CLASS));
}

function eligible(area) {
    return area.maxLength > 0
        && !area.readOnly
        && area.dataset.charCounter !== 'off'
        && !hasOwnCounter(area);
}

let nextId = 0;

function bind(area) {
    if (area.tagName !== 'TEXTAREA' || BOUND.has(area) || !eligible(area)) return;

    const counter = document.createElement('small');
    counter.className = COUNTER_CLASS;
    counter.id = `char-count-${++nextId}`;
    // Screen readers hear the full count on focus via aria-describedby; the
    // live region only speaks when the limit gets close, not on every keystroke.
    const live = document.createElement('span');
    live.className = 'visually-hidden';
    live.setAttribute('aria-live', 'polite');

    area.insertAdjacentElement('afterend', counter);
    counter.insertAdjacentElement('afterend', live);
    const describedBy = area.getAttribute('aria-describedby');
    area.setAttribute('aria-describedby', describedBy ? `${describedBy} ${counter.id}` : counter.id);

    const state = { counter, live, wasNear: false };
    BOUND.set(area, state);
    area.addEventListener('input', () => update(area));
    area.addEventListener('focus', () => update(area));
    area.form?.addEventListener('reset', () => setTimeout(() => update(area)));
    update(area);
}

export function update(area) {
    const state = BOUND.get(area);
    if (!state) return;
    const max = area.maxLength;
    if (!(max > 0)) {
        state.counter.hidden = true;
        return;
    }
    const left = Math.max(0, max - area.value.length);
    const near = left <= nearThreshold(max);
    const text = `${left.toLocaleString('en-US')} of ${max.toLocaleString('en-US')} characters left`;
    state.counter.hidden = false;
    state.counter.textContent = text;
    state.counter.classList.toggle('is-near', near);
    state.counter.classList.toggle('is-full', left === 0);
    if (near && (!state.wasNear || left === 0)) state.live.textContent = text;
    state.wasNear = near;
}

function scan(root) {
    if (root.matches?.('textarea')) bind(root);
    root.querySelectorAll?.('textarea[maxlength]').forEach(bind);
}

export function initCharCounters(root = document) {
    scan(root);
    if (typeof MutationObserver === 'undefined' || !document.body) return;
    new MutationObserver(records => {
        for (const r of records) {
            if (r.type === 'attributes') {
                if (BOUND.has(r.target)) update(r.target);
                else bind(r.target);
            } else {
                r.addedNodes.forEach(n => { if (n.nodeType === 1) scan(n); });
            }
        }
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['maxlength'] });
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    domReady(() => initCharCounters());
}
